import React, { useEffect } from 'react';
import {
  Select,
  InputLabel,
  MenuItem,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@material-ui/core';
import X from '@jda/lui-common-icon-library/ui/X';
import { Palette } from '@jda/lui-common-component-library';
import ArrowChevronsUp from '@jda/lui-common-icon-library/ui/ArrowChevronsUp';
import ArrowChevronsDown from '@jda/lui-common-icon-library/ui/ArrowChevronsDown';
import ArrowChevronUp from '@jda/lui-common-icon-library/ui/ArrowChevronUp';
import ArrowChevronDown from '@jda/lui-common-icon-library/ui/ArrowChevronDown';
import ArrowChevronsLeft from '@jda/lui-common-icon-library/ui/ArrowChevronsLeft';
import ArrowChevronsRight from '@jda/lui-common-icon-library/ui/ArrowChevronsRight';
import ArrowChevronLeft from '@jda/lui-common-icon-library/ui/ArrowChevronLeft';
import ArrowChevronRight from '@jda/lui-common-icon-library/ui/ArrowChevronRight';
import { makeStyles } from '@material-ui/core/styles';
import { USER_CONFIGURATION_SETTINGS } from '../../containers/graphQL/queries';
import { useMutation } from '@apollo/react-hooks';
import { useSnackbar } from 'notistack';
import _ from 'lodash';

const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%'
  },
  dialogTitle: {
    padding: theme.spacing(2),
    borderBottom: '1px solid'
  },
  progress: {
    color: theme.palette.background.paper
  },
  headers: {
    color: theme.palette.text.secondary
  },
  wrapper: {
    backgroundColor: Palette.colors.cornflower['50']
  },
  mainGrid: {
    margin: theme.spacing(2),
    marginBottom: theme.spacing(0),
    backgroundColor: theme.palette.background.paper,
    width: 'auto',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
    borderRadius: theme.spacing(0.5)
  },
  grid: {
    border: '1px solid',
    borderColor: 'white',
    width: '100%',
    maxHeight: '51vh',
    borderRadius: theme.spacing(0.5)
  },
  header: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.5),
    paddingLeft: theme.spacing(0),
    fontWeight: 'bold',
    fontSize: theme.spacing(1.75)
  },
  button: {
    border: '1px solid',
    borderColor: 'white',
    marginBottom: '0.1vh',
    marginTop: '0.1vh'
  },
  buttonList: {
    marginTop: '7.5vh',
    float: 'right'
  },
  greyIcon: {
    color: 'grey'
  },
  wrap: {
    overflowY: 'auto',
    height: '44.1vh',
    marginTop: theme.spacing(1),
    border: `1px solid ${Palette.colors.grey['200']}`,
    borderRadius: theme.spacing(0.5),
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: Palette.colors.grey['600'],
      opacity: '0.5'
    }
  },
  table: {
    backgroundColor: theme.palette.background.paper
  },
  typographyLabel: {
    fontSize: theme.spacing(2),
    fontWeight: 'bold',
    color: theme.palette.text.primary
  },
  tableCell: {
    padding: '1vh',
    paddingLeft: theme.spacing(1),
    height: theme.spacing(5.25)
  },
  tableBody: {
    cursor: 'pointer'
  },
  emptyTableBody: {
    height: '100%',
    width: '100%',
    textAlign: 'center',
    paddingTop: '18vh',
    cursor: 'pointer'
  },
  buttonSection: {
    marginLeft: 'auto',
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    height: theme.spacing(8),
    paddingTop: theme.spacing(2)
  },
  margin: {
    marginRight: '2vh',
    float: 'right',
    '&:disabled': {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      opacity: 0.5
    }
  },
  buttons: {
    float: 'right'
  },
  textField: {
    width: '100%'
  },
  directionField: {
    width: '40%',
    paddingRight: theme.spacing(0)
  },
  label: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1)
  },
  inputLabel: {
    fontSize: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  labelForPref: {
    fontSize: theme.spacing(1.5),
    paddingTop: theme.spacing(2)
  },
  clearButton: {
    marginLeft: theme.spacing(1)
  },
  gridContainer: {
    padding: theme.spacing(1)
  },
  sortPreference: {
    padding: '1vh',
    marginTop: theme.spacing(0),
    backgroundColor: theme.palette.background.paper,
    width: 'auto',
    borderBottomLeftRadius: theme.spacing(0.5),
    borderBottomRightRadius: theme.spacing(0.5),
    marginRight: theme.spacing(2),
    paddingTop: theme.spacing(0)
  },
  sortPreferenceHeading: {
    padding: '1vh',
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    width: 'auto',
    paddingLeft: theme.spacing(1.5),
    paddingBottom: theme.spacing(0),
    borderTopLeftRadius: theme.spacing(0.5),
    borderTopRightRadius: theme.spacing(0.5)
  },
  typography: {
    fontWeight: 'bold',
    paddingTop: theme.spacing(0.75),
    fontSize: theme.spacing(1.75)
  },
  iconButton: {
    float: 'right',
    marginRight: theme.spacing(0.5)
  },
  container: {
    marginBottom: theme.spacing(2)
  },
  gridItem: {
    marginTop: theme.spacing(2)
  },
  flexContainer: {
    maxHeight: '100%',
    overflow: 'hidden',
    height: '100%',
    borderBottomLeftRadius: theme.spacing(0.5),
    borderBottomRightRadius: theme.spacing(0.5)
  },
  box: {
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2)
  },
  tableRow: {
    '&$selected, &$selected:hover': {
      backgroundColor: `${Palette.colors.byBlue['50']} !important`
    },
    '&:nth-child(even):not(.materialTableRowRef)': {
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: Palette.colors.grey['100']
      }
    },
    '&:nth-child(odd):not(.materialTableRowRef)': {
      '&:hover': {
        backgroundColor: Palette.colors.grey['100']
      }
    }
  },
  dialogContent: {
    padding: theme.spacing(0),
    backgroundColor: Palette.colors.cornflower['50']
  },
  selected: {}
}));

const CustomGrid = (props: any) => {
  const sortColumn1 = 'sortColumn';
  const sortOrder1 = 'sortOrder';
  const classes = useStyles();
  let { participantType, workflowCode } = props;

  const { enqueueSnackbar } = useSnackbar();

  const [userConfigurationSettings] = useMutation(USER_CONFIGURATION_SETTINGS);

  const [availableRowList, setAvailableRowList] = React.useState<any>([]);
  const [selectedRowList, setSelectedRowList] = React.useState<any>([]);
  const [selectedColumnList, setSelectedColumnList] = React.useState<any>([]);
  const [availableColumnList, setAvailableColumnList] = React.useState<any>([]);
  const [sortPrefs, setSortPrefs] = React.useState<any>(props.sortPrefs);
  useEffect(() => {
    setSelectedColumnList(props.availableColumnList);
    if (props?.userSelPrefsColumns?.length > 0) setAvailableColumnList(props.userSelPrefsColumns);
    setSortPrefs(props.sortPrefs);
  }, [props.availableColumnList, props.sortPrefs, props.userSelPrefsColumns]);

  const isAvailableRowClicked = (id: any) => {
    const list = availableRowList.filter((index: any) => index === id);
    if (list.length > 0) {
      return true;
    }
    return false;
  };

  const isSelectedRowClicked = (id: any) => {
    const list = selectedRowList.filter((index: any) => index === id);
    if (list.length > 0) {
      return true;
    }
    return false;
  };

  const handleSelectedRowClick = (id: any, event: any) => {
    let selectedList = [];
    if (!isSelectedRowClicked(id)) {
      selectedList = [...selectedRowList];
      selectedList.push(id);
    } else {
      selectedList = selectedRowList.filter((index: any) => index !== id);
    }

    setSelectedRowList(selectedList);
  };

  const handleRightArrow = () => {
    const list = [...selectedColumnList];
    const columnList = availableColumnList.filter((row: any, index: number) => {
      if (isAvailableRowClicked(index)) {
        list.push(row);
        return false;
      }
      return true;
    });
    setAvailableColumnList(columnList);
    setSelectedColumnList(list);
    setAvailableRowList([]);
  };

  const handleLeftArrow = () => {
    const list = [...availableColumnList];
    const columnList = selectedColumnList.filter((row: any, index: number) => {
      if (isSelectedRowClicked(index)) {
        list.push(row);
        return false;
      }
      return true;
    });
    setSelectedColumnList(columnList);
    setAvailableColumnList(list);
    setSelectedRowList([]);
  };

  const handleAvailableRowClick = (id: any, event: any) => {
    let selectedList = [];
    if (!isAvailableRowClicked(id)) {
      selectedList = [...availableRowList];
      selectedList.push(id);
    } else {
      selectedList = availableRowList.filter((index: any) => index !== id);
    }
    setAvailableRowList(selectedList);
  };

  const handleUpwardArrow = () => {
    if (selectedRowList.length === 1 && selectedColumnList.length > 1) {
      const list = [...selectedColumnList];
      let newId: any = '';
      list.forEach((row: any, index: number) => {
        if (selectedRowList[0] === index && index > 0) {
          const temp = list[index];
          list[index] = list[index - 1];
          list[index - 1] = temp;
          newId = index - 1;
        }
      });
      selectedRowList[0] = newId;
      setSelectedColumnList(list);
      setSelectedRowList(selectedRowList);
    }
  };

  const handleDownwardArrow = () => {
    if (selectedRowList.length === 1 && selectedColumnList.length > 1) {
      const list = [...selectedColumnList];
      let newId: any = '';
      list.forEach((row: any, index: number) => {
        if (selectedRowList[0] === index && index < list.length - 1) {
          const temp = list[index];
          list[index] = list[index + 1];
          list[index + 1] = temp;
          newId = index + 1;
        }
      });
      selectedRowList[0] = newId;
      setSelectedColumnList(list);
      setSelectedRowList(selectedRowList);
    }
  };

  const handleDoubleUpwardArrow = () => {
    if (selectedRowList.length === 1) {
      const obj1: any = [];
      const obj2: any = [];
      selectedColumnList.map((row: any, index: number) => {
        if (index === selectedRowList[0]) {
          return obj1.push(row);
        } else {
          return obj2.push(row);
        }
      });
      const list = [...obj1, ...obj2];
      selectedRowList[0] = 0;
      setSelectedColumnList(list);
      setSelectedRowList(selectedRowList);
    }
  };

  const handleDoubleDownwardArrow = () => {
    if (selectedRowList.length === 1) {
      const obj1: any = [];
      const obj2: any = [];
      selectedColumnList.map((row: any, index: number) => {
        if (index === selectedRowList[0]) {
          return obj1.push(row);
        } else {
          return obj2.push(row);
        }
      });
      const list = [...obj2, ...obj1];
      selectedRowList[0] = [...selectedColumnList].length - 1;
      setSelectedColumnList(list);
      setSelectedRowList(selectedRowList);
    }
  };

  const handleDoubleRightArrow = () => {
    const list = [...selectedColumnList];
    availableColumnList.map((row: any) => {
      return list.push(row);
    });
    setSelectedColumnList(list);
    setAvailableColumnList([]);
  };

  const handleDoubleLeftArrow = () => {
    if (!(selectedRowList.length > 0) && selectedColumnList.length !== 0) {
      const list = [...availableColumnList];
      selectedColumnList.map((row: any) => {
        return list.push(row);
      });
      setSelectedColumnList([]);
      setAvailableColumnList(list);
    }
  };

  const getMenuItems = (columns: any) => {
    return columns.map((option: any) => (
      <MenuItem key={option.field} value={option.field}>
        {option.title}
      </MenuItem>
    ));
  };

  const displayMenuItems = (columnDefs: any) => {
    return columnDefs ? getMenuItems(columnDefs) : getMenuItems(columnDefs);
  };

  const saveSortPreference = (event: any, name: any) => {
    let prefs: any = {};
    prefs = { ...sortPrefs };
    prefs[event.target.name] = event.target.value;
    setSortPrefs(prefs);
  };
  const handleDialogSave = (selectedColumnList: any, sortPref: any) => {
    handleDialogOK(selectedColumnList, sortPref);

    let configuration_key = '';
    const columns: any = [];
    let columnDef: any = [];

    selectedColumnList.map((obj: any) => {
      columnDef = { ...obj };
      return columns.push(columnDef);
    });

    if (participantType && workflowCode) {
      // console.log('Column Length: '+columns?.length,participantType,workflowCode);
      configuration_key = participantType + '-' + workflowCode;
    } else if (participantType === 'CUSTOMER' || participantType === 'PROVIDER') {
      // console.log('Column Length: '+columns?.length,participantType,workflowCode);
      configuration_key = participantType + '-' + 'global';
    } else if (participantType === 'ADMIN') {
      // console.log('Column Length: '+columns?.length,participantType,workflowCode,props.isAdminLevel);
      configuration_key = 'global-global';
    }

    // let userPreferenceColumns: any = {};
    // let userPreferenceOtherSettings: any = {};
    // if (props.isAdminLevel) {
    //   const updatedColumn = columns;
    //   userPreferenceColumns = { ...props.existingUserColumns };
    //   userPreferenceColumns[participantType] = updatedColumn;
    //   userPreferenceOtherSettings = { ...props.existingUserSortPrefs };
    //   userPreferenceOtherSettings[participantType] = sortPref;
    // } else {
    //   const updatedColumn = { ...props.existingUserColumns?.[participantType], ...{ [workflowCode]: columns } };
    //   userPreferenceColumns = { ...props.existingUserColumns };
    //   userPreferenceColumns[participantType] = updatedColumn;
    //   const updatedSortPrefs = { ...props.existingUserSortPrefs?.[participantType], ...{ [workflowCode]: sortPref } };
    //   userPreferenceOtherSettings = { ...props.existingUserSortPrefs };
    //   userPreferenceOtherSettings[participantType] = updatedSortPrefs;
    // }

    if (sortPref) {
      if (sortPref[sortColumn1] && !sortPref[sortOrder1]) {
        sortPref[sortOrder1] = 'asc';
      }
    }
    const settings = {
      columns: columns || [],
      sortPrefs: sortPref || {}
    };

    userConfigurationSettings({
      variables: {
        applicationId: 'BYN-SelfService',
        key: configuration_key + '-error-diagnosis',
        hierarchy: 'USER',
        configuration: settings
      }
    })
      .then(response1 => {
        enqueueSnackbar('User prefrences have been saved successfully !!', {
          variant: 'success',
          preventDuplicate: true
        });
        props.refresh();
        props.refetch();
        return response1;
      })
      .catch(error => {
        enqueueSnackbar('Unable to save', { variant: 'error', preventDuplicate: true });
      });
  };

  const handleDialogOK = (selectedColumnList: any, sortPref: any) => {
    props.handleSubmit(selectedColumnList, sortPref);
    if (sortPref) {
      if (sortPref[sortColumn1] && !sortPref[sortOrder1]) {
        sortPref[sortOrder1] = 'asc';
      }
    }
    setSortPrefs(sortPref);
  };

  const handleDialogClose = () => {
    props.handleDialogClose();
    setSelectedColumnList(selectedColumnList);
    if (props?.userSelPrefsColumns?.length > 0) setAvailableColumnList(props.userSelPrefsColumns);
    setSortPrefs(props.sortPrefs);
  };

  const handleDialogReset = () => {
    setSelectedColumnList(props.availableColumnList);
    setAvailableColumnList(props.userSelPrefsColumns);
    setSortPrefs(props.sortPrefs);
  };

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={props.dialogOpen}
      onClose={props.handleDialogClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      id="custom-grid-dialog"
    >
      <DialogTitle id="customgrid-dialog-title" className={classes.dialogTitle}>
        <Grid container alignItems="center">
          <Grid item xs={7}>
            <Typography className={classes.typographyLabel} variant="h3">
              Column Selection
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <IconButton onClick={props.handleDialogClose} className={classes.iconButton}>
              <X />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div id={props.title} key={props.title} className={classes.wrapper}>
          <Grid container className={classes.container}>
            <Grid item xs={8}>
              <Grid container id="maingrid" className={classes.mainGrid} justify="space-between">
                <Grid item xs={5} className={classes.grid}>
                  <Typography variant="h6" className={classes.header}>
                    Available
                  </Typography>
                  <div className={selectedColumnList.length >= 0 ? classes.wrap : ''}>
                    <Table className={classes.table}>
                      <TableBody className={classes.tableBody} data-id="available-column">
                        {availableColumnList.map((row: any, index: number) => (
                          <TableRow
                            key={index}
                            data-id="available-field"
                            id={`${row.field}-available`}
                            onClick={e => handleAvailableRowClick(index, e)}
                            selected={isAvailableRowClicked(index)}
                            className={classes.tableRow}
                            classes={{ selected: classes.selected }}
                          >
                            <TableCell className={classes.tableCell}>
                              <Typography>{row.title}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Grid>
                <Grid item xs={1}>
                  <Grid
                    container
                    direction="column"
                    justify="center"
                    alignContent="center"
                    alignItems="center"
                    className={classes.buttonList}
                  >
                    <Grid item xs={12}>
                      <div
                        id="faAngleDoubleUp"
                        data-id="faAngle-DoubleUp"
                        className={classes.button}
                        onClick={() => handleDoubleUpwardArrow()}
                      >
                        <ArrowChevronsUp className={selectedRowList.length !== 1 ? classes.greyIcon : ''} />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div
                        id="faAngleUp"
                        data-id="faAngleUp"
                        className={classes.button}
                        onClick={() => handleUpwardArrow()}
                      >
                        <ArrowChevronUp
                          className={
                            selectedRowList.length !== 1 || selectedColumnList.length === 1 || selectedRowList[0] === 0
                              ? classes.greyIcon
                              : ''
                          }
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div
                        id="faAngleRight"
                        data-id="right-arrow"
                        className={classes.button}
                        onClick={() => handleRightArrow()}
                      >
                        <ArrowChevronRight className={availableRowList.length === 0 ? classes.greyIcon : ''} />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div
                        id="faAngleDoubleRight"
                        data-id="faAngleDoubleRight"
                        className={classes.button}
                        onClick={() => handleDoubleRightArrow()}
                      >
                        <ArrowChevronsRight
                          className={
                            availableRowList.length > 0 || availableColumnList.length === 0 ? classes.greyIcon : ''
                          }
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div
                        id="faAngleLeft"
                        data-id="left-arrow"
                        className={classes.button}
                        onClick={() => handleLeftArrow()}
                      >
                        <ArrowChevronLeft className={selectedRowList.length === 0 ? classes.greyIcon : ''} />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div
                        id="faAngleDoubleLeft"
                        data-id="faAngleDoubleLeft"
                        className={classes.button}
                        onClick={() => handleDoubleLeftArrow()}
                      >
                        <ArrowChevronsLeft
                          className={
                            selectedRowList.length > 0 || selectedColumnList.length === 0 ? classes.greyIcon : ''
                          }
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div
                        id="faAngleDown"
                        data-id="faAngleDown"
                        className={classes.button}
                        onClick={() => handleDownwardArrow()}
                      >
                        <ArrowChevronDown
                          className={
                            selectedRowList.length !== 1 ||
                            selectedColumnList.length === 1 ||
                            selectedRowList[0] === selectedColumnList.length - 1
                              ? classes.greyIcon
                              : ''
                          }
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div
                        id="faAngleDoubleDown"
                        data-id="faAngleDoubleDown"
                        className={classes.button}
                        onClick={() => handleDoubleDownwardArrow()}
                      >
                        <ArrowChevronsDown className={selectedRowList.length !== 1 ? classes.greyIcon : ''} />
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={5} className={classes.grid}>
                  <Typography variant="h6" className={classes.header}>
                    Selected
                  </Typography>
                  <div className={classes.wrap}>
                    <Table className={classes.table}>
                      <TableBody className={classes.tableBody} data-id="selected-column">
                        {selectedColumnList.map((row: any, index: any) => (
                          <TableRow
                            key={index}
                            data-id="selected-column-field"
                            id={`${row.field}-selected`}
                            onClick={e => handleSelectedRowClick(index, e)}
                            selected={isSelectedRowClicked(index)}
                            className={classes.tableRow}
                            classes={{ selected: classes.selected }}
                          >
                            <TableCell className={classes.tableCell}>
                              <Typography>{row.title}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={4} className={classes.gridItem}>
              <Grid container className={classes.flexContainer} alignItems="flex-start" direction="row" spacing={0}>
                <Grid container alignItems="center">
                  <Grid item xs={12} className={classes.sortPreferenceHeading}>
                    <Typography className={classes.typography} variant="h6">
                      Other Preferences
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container style={{ height: '100%' }} alignItems="flex-start" spacing={0}>
                  <Grid container className={classes.sortPreference} direction="row" alignItems="center">
                    <Grid container className={classes.gridContainer} justify="space-between" spacing={1}>
                      <Grid item xs={5}>
                        <Grid container alignItems="center">
                          <InputLabel htmlFor="age-simple" className={classes.inputLabel}>
                            Sort Column
                          </InputLabel>
                        </Grid>
                        <Select
                          id="Select-SortColumn-1"
                          data-id="Select-SortColumn-1"
                          className={classes.textField}
                          value={sortPrefs[sortColumn1] || ''}
                          onChange={e => saveSortPreference(e, 'sortColumn')}
                          inputProps={{
                            name: sortColumn1
                          }}
                        >
                          {displayMenuItems(selectedColumnList)}
                        </Select>
                      </Grid>
                      <Grid item xs={6}>
                        <Grid container alignItems="center">
                          <InputLabel htmlFor="age-simple" className={classes.inputLabel}>
                            Sort Order
                          </InputLabel>
                        </Grid>
                        <Select
                          id="Select-SortOrder-1"
                          data-id="Select-SortOrder-1"
                          className={classes.textField}
                          onChange={e => saveSortPreference(e, 'sortOrder')}
                          value={sortPrefs[sortOrder1] || ''}
                          inputProps={{
                            name: sortOrder1
                          }}
                        >
                          <MenuItem value="ASC">Asc</MenuItem>
                          <MenuItem value="DESC">Desc</MenuItem>
                        </Select>
                      </Grid>
                    </Grid>
                    <Grid container className={classes.gridContainer} justify="space-between" spacing={1}>
                      <Grid item xs={5}>
                        <Grid container alignItems="center">
                          <InputLabel htmlFor="age-simple" className={classes.inputLabel}>
                            Duration of Events
                          </InputLabel>
                        </Grid>
                        <Select
                          id="Select-duration-event"
                          data-id="Select-DurationOf-Event"
                          className={classes.textField}
                          value={sortPrefs['duration'] || ''}
                          onChange={e => saveSortPreference(e, 'duration')}
                          inputProps={{
                            name: 'duration'
                          }}
                        >
                          {['1 Day', '1 Week', '15 Days', '30 Days'].map((item: any) => [
                            <MenuItem value={item}>{item}</MenuItem>
                          ])}
                        </Select>
                      </Grid>
                      <Grid item xs={6}>
                        <Grid container alignItems="center">
                          <InputLabel htmlFor="age-simple" className={classes.inputLabel}>
                            Page Size
                          </InputLabel>
                        </Grid>
                        <Select
                          label="Page Size"
                          id="Select-page-size"
                          data-id="Select-page-size"
                          className={classes.textField}
                          value={sortPrefs['pageNumber'] || ''}
                          onChange={e => saveSortPreference(e, 'pageNumber')}
                          inputProps={{
                            name: 'pageNumber'
                          }}
                        >
                          {[10, 20, 50, 100, 200].map((item: any) => [<MenuItem value={item}>{item}</MenuItem>])}
                        </Select>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container className={classes.box}></Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <div className={classes.buttonSection}>
          <Button
            className={classes.buttons}
            data-id="save-Button"
            onClick={() => handleDialogSave(selectedColumnList, sortPrefs)}
            disabled={false}
            color="primary"
            autoFocus
            variant="outlined"
          >
            Save
          </Button>
          <Button
            data-id="apply"
            className={classes.margin}
            autoFocus
            onClick={() => handleDialogOK(selectedColumnList, sortPrefs)}
            disabled={selectedColumnList.length === 0}
            color="primary"
            variant="outlined"
          >
            Apply
          </Button>
          <Button
            data-id="reset-Button"
            className={classes.margin}
            onClick={() => handleDialogReset()}
            color="primary"
            autoFocus
            variant="outlined"
          >
            Reset
          </Button>
          <Button
            data-id="cancel-Button"
            className={classes.margin}
            onClick={() => handleDialogClose()}
            color="primary"
            autoFocus
            variant="outlined"
          >
            Cancel
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
export default CustomGrid;
